/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, InputPrice, Select, Loading, Datepicker} from '../../../../components'
import {PageTitle} from '../../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {projectLookup, basicLookup} from '../../../../redux/slices/lookup.slice'
import {
  expenseinvoiceSundryUpdate,
  expenseinvoiceSundryGetId,
  setIsRedirect,
} from '../../../../redux/slices/expenseinvoice.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  InvoiceKind: state.lookup.InvoiceKind,
  loading: state.lookup.loading
})

const selectExpenseInvoiceData = (state) => ({
  isRedirect: state.expenseinvoice.isRedirect,
  detail: state.expenseinvoice.detail,
  loading: state.expenseinvoice.loading
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {projectLookupList, InvoiceKind, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectExpenseInvoiceData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(expenseinvoiceSundryGetId({id}))
    dispatch(basicLookup('InvoiceKind'))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() =>
    Yup.object().shape({
      Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      InvoiceKindId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    }), [intl]
  )

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    ProjectId: '',
    EditDate: new Date(),
    DefermentDate: new Date(),
    TotalPrice: 0,
    InvoiceKindId: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(expenseinvoiceSundryUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: detail
      ? {
          Id: detail.id || '',
          Name: detail.name || '',
          ProjectId: detail.projectId || '',
          EditDate: detail.editDate ? new Date(detail.editDate) : new Date(),
          DefermentDate: detail.defermentDate ? new Date(detail.defermentDate) : new Date(),
          TotalPrice: detail.totalPrice || 0,
          InvoiceKindId: detail.invoiceKindId || '',
        }
      : formValues,
    enableReinitialize: true,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.EXPENSES'}),
      path: '/expenses/expenses',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/expenses/expenses/sundry/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/detail/${id}`)
      dispatch(setIsRedirect(false))
    }
  }, [isRedirect, navigate, pageUrl, id, dispatch])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.EXPENSES.SUNDRY.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATESUNDRY.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATESUNDRY.PROJECT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={projectLookupList}
                    required
                    {...formik.getFieldProps('ProjectId')}
                    onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
                  />
                  {formik.touched.ProjectId && formik.errors.ProjectId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProjectId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICEKINDNAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={InvoiceKind}
                    required
                    {...formik.getFieldProps('InvoiceKindId')}
                    onChange={(item) => formik.setFieldValue('InvoiceKindId', Number(item && item.value))}
                  />
                  {formik.touched.InvoiceKind && formik.errors.InvoiceKind && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.InvoiceKind}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATESUNDRY.EDITDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('EditDate')}
                    onChange={([date]) => formik.setFieldValue('EditDate', date)}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATESUNDRY.DEFERMENTDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('DefermentDate')}
                    onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATESUNDRY.TOTALPRICE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('TotalPrice')}
                    onChange={(value) => formik.setFieldValue('TotalPrice', Number(value))}
                  />
                </div>
              </div>
              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() =>
                      navigate(`/expenses/expenses/sundry/detail/${id}`)
                    }
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
